import { useState, type ReactNode } from 'react';
import { Button, Card, Divider } from 'antd';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import type { FieldDefinitionDto } from '@crm/shared';
import { CardTitle } from './StatCard';

export type FieldGroups = [string, FieldDefinitionDto[]][];

/** Groups fields by their `group`, split into main sections and the "View more" (moreInfo) ones. */
export function splitFieldGroups(defs: FieldDefinitionDto[], skip: (d: FieldDefinitionDto) => boolean = () => false) {
  const main = new Map<string, FieldDefinitionDto[]>();
  const more = new Map<string, FieldDefinitionDto[]>();
  for (const d of defs) {
    if (skip(d)) continue;
    const map = d.moreInfo ? more : main;
    const g = d.group ?? 'Other';
    map.set(g, [...(map.get(g) ?? []), d]);
  }
  return { main: [...main.entries()] as FieldGroups, more: [...more.entries()] as FieldGroups };
}

export function viewMoreLabel(open: boolean, count: number) {
  return open ? 'Show less' : `View more (${count} field${count === 1 ? '' : 's'})`;
}

/** Collapsed card at the bottom of a profile holding the less-used fields, still grouped by their `group`. */
export function ViewMoreCard({ groups, render }: { groups: FieldGroups; render: (fields: FieldDefinitionDto[]) => ReactNode }) {
  const [open, setOpen] = useState(false);
  if (!groups.length) return null;
  const count = groups.reduce((n, [, f]) => n + f.length, 0);
  return (
    <Card
      title={
        <div role="button" tabIndex={0} aria-expanded={open} onClick={() => setOpen(!open)} onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), setOpen(!open))} style={{ cursor: 'pointer' }}>
          <CardTitle title={open ? 'More information' : 'View more'} subtitle={`${count} less-used field${count === 1 ? '' : 's'}`} />
        </div>
      }
      extra={<Button type="text" size="small" icon={open ? <UpOutlined /> : <DownOutlined />} aria-label={viewMoreLabel(open, count)} onClick={() => setOpen(!open)} />}
      style={{ marginBottom: 24 }}
      styles={{ body: open ? undefined : { display: 'none' } }}
    >
      {open &&
        groups.map(([group, fields], i) => (
          <div key={group}>
            {groups.length > 1 && (
              <Divider titlePlacement="start" plain style={{ marginTop: i === 0 ? 0 : 12 }}>
                {group}
              </Divider>
            )}
            {render(fields)}
          </div>
        ))}
    </Card>
  );
}
